import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./treatmentDropDownItemNew.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { setTreatmentViewData } from "../../../app/patientSlice";

export const TreatmentDropDownItemNew = ({
  dressing,
  id = "",
  name = "",
  label = "",
  listOptions,
  required = false,
  dropValue,
  setGetValueDrop,
  getData,
  classes,
  disabled,
}) => {
  const dispatch = useDispatch();
  const dropRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const treatmentViewData = useSelector((state) => state.patient.treatmentViewData);

  const selected = useMemo(() => {
    return (
      treatmentViewData?.filter((item) => item.key === name && "value" in item).map(({ value, isRecommended }) => ({ value, isRecommended })) || []
    );
  }, [treatmentViewData, name]);

  const textValue = useMemo(() => {
    if (id !== "-1") return "";
    const entry = treatmentViewData?.find((item) => item.key === name);
    return entry?.value ?? "";
  }, [treatmentViewData, name, id]);

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const filtered = treatmentViewData?.filter((i) => i.key !== name) || [];
    const updated = newValue ? [...filtered, { key: name, value: newValue }] : filtered;
    dispatch(setTreatmentViewData(updated));
    getData?.();
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const updateTreatmentViewData = (updated) => {
    const newEntries = updated?.map((v) => ({
      key: name,
      value: v?.value,
      isRecommended: v?.isRecommended,
    }));

    const filtered = treatmentViewData?.filter((i) => i.key !== name) || [];
    dispatch(setTreatmentViewData([...filtered, ...newEntries]));
    getData?.();
  };

  const isSelected = (val) => selected.some((s) => s.value === val);

  const toggleItem = (opt, e) => {
    e.stopPropagation();

    const updated = isSelected(opt.materialNumber)
      ? selected.filter((s) => s.value !== opt.materialNumber)
      : [...selected, { value: opt.materialNumber, isRecommended: opt.isRecommended || 0 }];

    updateTreatmentViewData(updated);

    if (!open) setOpen(true);

    if (dressing === "secondary" && dropValue === true) {
      setGetValueDrop(false);
    }
  };

  const removeCategory = (val, e) => {
    e.stopPropagation();
    const updated = selected.filter((s) => s.value !== val);
    updateTreatmentViewData(updated);
  };

  const optionMap = useMemo(() => {
    const map = new Map();
    listOptions.forEach((o) => map.set(o.materialNumber, o));
    return map;
  }, [listOptions]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return listOptions;

    const searchText = search.toLowerCase();

    return listOptions?.filter(
      (opt) =>
        opt?.materialCode?.toLowerCase().includes(searchText) ||
        opt?.materialDescription?.toLowerCase().includes(searchText) ||
        opt?.productFamilyDesc?.toLowerCase().includes(searchText)
    );
  }, [search, listOptions]);

  const handleClearAll = () => {
    updateTreatmentViewData([]);
    setSearch("");
  };

  return (
    <div className={classes?.column ? classes?.column : "row align-items-start p-2"}>
      <div className="col-5">
        {label && (
          <label htmlFor={id} className={`${classes.labelClassName} ${styles.text_field_label}`}>
            {label}:{required && <span className={styles.astrick}> *</span>}
          </label>
        )}
      </div>

      <div className={`${classes ? classes.inputColumn : ""}`}>
        {id === "-1" ? (
          <input
            id={id}
            type="text"
            className={`form-control ${disabled ? styles.disabled : ""}`}
            value={textValue}
            disabled={disabled}
            onChange={handleTextChange}
          />
        ) : (
          <div className={styles.wrapper} ref={dropRef}>
            <div
              className={`${styles.dropdown_toggle} ${disabled ? styles.disabled : ""}`}
              onClick={() => {
                if (disabled) return;
                setOpen((o) => !o);
              }}
            >
              <div className={styles.selectedWrapper}>
                {selected.length === 0 ? (
                  <span className={styles.placeholder}>Select</span>
                ) : (
                  selected.map((s) => {
                    const opt = optionMap.get(s.value);
                    return (
                      <div key={s.value} className={styles.chip} onClick={(e) => e.stopPropagation()}>
                        <span className={styles.chipText}>{opt?.materialCode}</span>
                        <button type="button" className={styles.chip_close} onClick={(e) => removeCategory(s.value, e)}>
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              <div className={styles.dropdown_arrow}>{open ? "▲" : "▼"}</div>
            </div>

            {open && (
              <div className={styles.dropdown_menu}>
                <div className={styles.searchBox}>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {search && (
                    <button type="button" className={styles.clearSearch} onClick={() => setSearch("")}>
                      <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
                <div className={styles.menu_scroll}>
                  {filteredOptions?.map((opt) => {
                    const checked = isSelected(opt.materialNumber);
                    return (
                      <div
                        key={opt.materialNumber}
                        className={`${styles.item} ${checked ? styles.active : ""}`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => toggleItem(opt, e)}
                      >
                        <div className="d-flex align-items-center">
                          <div className={styles.checkboxContainer}>
                            <input type="checkbox" checked={checked} readOnly />
                          </div>
                          <div className={styles.itemContent}>
                            <div className={styles.cat}>{opt.productFamilyDesc}</div>
                            <div className={styles.material_description}>
                              <div>{opt.materialDescription}</div>
                            </div>
                            {dressing !== "secondary" && opt?.isRecommended === 1 && <div className={styles.recommended}>Recommended</div>}
                            <div className={styles.material_code}>{opt.materialCode}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.dropdown_footer}>
                  <span className={styles.selected_count}>Selected: {selected.length}</span>
                  <button type="button" className={styles.clear_all_btn} onClick={handleClearAll} disabled={selected.length === 0}>
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
